/**
 * The same tokens as `tokens.css`, as data.
 *
 * The widget inlines its styles into a constructed stylesheet rather than
 * importing a CSS file, so it needs these as strings. Keeping both in one
 * package means a colour change lands in both places or neither.
 */

export const lightTokens = {
  "--verb-bg": "#ffffff",
  // 🔴 NEUTRAL, NOT WARM. The cream pair before this (#f8f5ec / #f1ede0, and
  // #fdfaf1 / #faf5e8 before them) kept reading as stained next to a
  // customer's own white UI, and the warm borders carried the same tint. This
  // is the same move the landing page and dashboard made on 2026-09-12:
  // separation comes from hairlines and shadow, never from yellow.
  "--verb-surface": "#f7f8fa",
  "--verb-surface-alt": "#eef0f4",
  "--verb-border": "#e6e8ec",
  "--verb-border-strong": "#adafb8",
  // The brand default, matching askverb.com. A site that sets its own
  // brandColor never sees these: deriveBrandTokens recomputes the four from it.
  "--verb-gold": "#ffc400",
  "--verb-gold-hover": "#f2b705",
  "--verb-gold-soft": "#fff6d9",
  "--verb-gold-ring": "#ffdf85",
  /** Text drawn ON a solid --verb-gold fill: the launcher, send, confirm.
   *  Defaults to --verb-text because near-black on the default gold is
   *  ~8.5:1; deriveBrandTokens recomputes this per custom colour so a site
   *  that picks a dark brand colour does not silently fail contrast. */
  "--verb-gold-fg": "#1c1917",
  "--verb-text": "#1c1917",
  // Neutral grey, not the warm brown-grey it replaced (#6b6152).
  "--verb-text-muted": "#5c6472",
  "--verb-text-inverse": "#fffdf7",
  "--verb-danger": "#b4341f",
  "--verb-danger-soft": "#fdecea",
  "--verb-success": "#2f7a43",
  "--verb-success-soft": "#eaf6ee",
  "--verb-radius": "12px",
  "--verb-radius-sm": "8px",
  /* Two layers, same reasoning as the dashboard's --shadow-card: a tight
     contact shadow so the edge sits ON the page, and a wide soft one for the
     lift. A single large blur reads as a glow. */
  "--verb-shadow":
    "0 1px 2px rgb(28 25 23 / 6%), 0 16px 44px -12px rgb(28 25 23 / 24%)",
  "--verb-font": '"Verb Geist", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "--verb-font-mono": 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
} as const satisfies Record<string, string>;

export type TokenName = keyof typeof lightTokens;

/** Renders the token block for injection into the widget's shadow root. */
export function tokensToCss(scope = ":host"): string {
  const body = Object.entries(lightTokens)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join("\n");
  return `${scope} {\n${body}\n}`;
}

// ── A customer's own brand colour, safely ──────────────────────────────────

/**
 * A site picks ONE colour. Everywhere that colour is used, hover states, wash
 * backgrounds, focus rings, text drawn on top of it, has to come from
 * somewhere, and asking a non-technical site owner to pick four coordinated
 * shades is not a real option. This derives all of them from the one hex
 * value, the same relationship the hand-picked gold shades already have to
 * each other.
 *
 * 🔴 THE TEXT COLOUR IS COMPUTED, NEVER FIXED, AND THAT IS NOT DECORATION.
 *
 *    `.launcher`, `.send` and `.btn-primary` paint text directly onto the
 *    solid brand colour. The default gold was hand-verified at ~8.5:1 with
 *    near-black text specifically because white on gold is ~2:1 and fails
 *    WCAG AA outright, per the note in styles.ts. A site that picks its own
 *    colour has no such guarantee: a dark navy or a saturated red needs
 *    LIGHT text, not the near-black the default uses. Picking whichever of
 *    near-black or near-white actually passes against the chosen colour is
 *    what keeps an arbitrary brand colour from silently shipping
 *    unreadable buttons.
 */
export function deriveBrandTokens(hex: string): Partial<Record<TokenName, string>> {
  const rgb = hexToRgb(hex);
  if (!rgb) return {};

  const DARK = "#1c1917";
  const LIGHT = "#fffdf7";
  const fg = contrastRatio(rgb, hexToRgb(DARK)!) >= contrastRatio(rgb, hexToRgb(LIGHT)!) ? DARK : LIGHT;

  return {
    // rgbToHex, not the raw input: a caller can pass "3b82f6" with no leading
    // #, which is not valid CSS on its own. Round-tripping through parsed RGB
    // guarantees a well-formed #rrggbb regardless of how it arrived.
    "--verb-gold": rgbToHex(rgb),
    "--verb-gold-hover": rgbToHex(mix(rgb, { r: 0, g: 0, b: 0 }, 0.16)),
    "--verb-gold-soft": rgbToHex(mix(rgb, { r: 255, g: 255, b: 255 }, 0.92)),
    "--verb-gold-ring": rgbToHex(mix(rgb, { r: 255, g: 255, b: 255 }, 0.55)),
    "--verb-gold-fg": fg,
  };
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function hexToRgb(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const c = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Linear interpolation toward `target` by `amount` (0 = no change, 1 = target). */
function mix(from: Rgb, target: Rgb, amount: number): Rgb {
  return {
    r: from.r + (target.r - from.r) * amount,
    g: from.g + (target.g - from.g) * amount,
    b: from.b + (target.b - from.b) * amount,
  };
}

/** WCAG relative luminance. */
function relativeLuminance({ r, g, b }: Rgb): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two colours, always >= 1. */
function contrastRatio(a: Rgb, b: Rgb): number {
  const l1 = relativeLuminance(a);
  const l2 = relativeLuminance(b);
  const [lighter, darker] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (lighter + 0.05) / (darker + 0.05);
}

// ── A customer's own background, safely ────────────────────────────────────

/**
 * The colour of the widget itself, as opposed to the colour of its buttons.
 *
 * 🔴 ONE INPUT, AND EVERY OTHER NEUTRAL FOLLOWS FROM IT, for the same reason
 *    deriveBrandTokens exists: nobody is going to hand-pick two surfaces, two
 *    borders and three text shades that agree with each other, and a panel
 *    where only the background moved is a panel with white cards floating on
 *    a dark ground.
 *
 * 🔴 THE TEXT COLOUR IS COMPUTED, WHICH IS THE WHOLE POINT. Letting somebody
 *    set a dark background while the text stayed near-black would ship an
 *    unreadable assistant, and the person who picked the colour would be the
 *    last to find out, because they picked it in a dashboard that is always
 *    light. Near-black or near-white, whichever actually passes against the
 *    chosen ground, exactly as the brand colour does for buttons.
 *
 * 🔴 SURFACES AND BORDERS ARE MIXED TOWARD THE TEXT, NOT TOWARD BLACK. On a
 *    dark background, mixing toward black makes the raised surfaces DARKER
 *    than the page behind them, which inverts the depth the design relies on.
 *    Toward the text colour, a light ground steps down and a dark ground steps
 *    up, and both read as "lifted" without a special case for either.
 */
export function deriveSurfaceTokens(hex: string): Partial<Record<TokenName, string>> {
  const bg = hexToRgb(hex);
  if (!bg) return {};

  const DARK = "#1c1917";
  const LIGHT = "#fffdf7";
  const darkRgb = hexToRgb(DARK)!;
  const lightRgb = hexToRgb(LIGHT)!;
  const towardDark = contrastRatio(bg, darkRgb) >= contrastRatio(bg, lightRgb);
  const text = towardDark ? darkRgb : lightRgb;
  const inverse = towardDark ? lightRgb : darkRgb;

  const step = (amount: number) => rgbToHex(mix(bg, text, amount));

  return {
    "--verb-bg": rgbToHex(bg),
    "--verb-surface": step(0.05),
    "--verb-surface-alt": step(0.1),
    "--verb-border": step(0.16),
    "--verb-border-strong": step(0.32),
    "--verb-text": rgbToHex(text),
    // Muted is the text pulled back TOWARD the ground rather than a grey, so
    // it keeps the same hue relationship on a tinted background.
    "--verb-text-muted": rgbToHex(mix(text, bg, 0.42)),
    "--verb-text-inverse": rgbToHex(inverse),
  };
}


// ── The two chat bubbles ───────────────────────────────────────────────────

/**
 * 🔴 DELIBERATELY NOT IN `lightTokens`, AND THAT IS THE WHOLE DESIGN.
 *
 *    The two bubbles have always been derived: the visitor's own is
 *    --verb-surface-alt, which follows the panel background, and the
 *    assistant's is --verb-gold-soft, which follows the brand colour. Giving
 *    them defaults in the token table would freeze both at a fixed hex and
 *    break that, so a site that picked a dark background would get a dark
 *    panel with two pale bubbles floating in it.
 *
 *    Instead the CSS reads `var(--verb-bubble-user, var(--verb-surface-alt))`.
 *    Unset means "keep following", which is a third state that a default
 *    cannot express, and it is the same reasoning migration 0013 gives for
 *    every appearance column being nullable.
 */
export type BubbleRole = "user" | "assistant";

export type BubbleTokenName =
  | "--verb-bubble-user"
  | "--verb-bubble-user-fg"
  | "--verb-bubble-assistant"
  | "--verb-bubble-assistant-fg";

/**
 * One colour in, the bubble and its text out.
 *
 * 🔴 THE TEXT IS COMPUTED HERE TOO, FOR THE THIRD TIME IN THIS FILE, because
 *    it is the third place somebody can pick a colour that text has to sit on.
 *    A bubble is the only one of the three where the text is a person's actual
 *    words rather than a label, so it is the worst one to get wrong: a deep
 *    blue reply bubble with near-black text is a message nobody can read, and
 *    the owner who chose it reads their own transcript in a dashboard that
 *    never renders it.
 */
export function deriveBubbleTokens(
  role: BubbleRole,
  hex: string,
): Partial<Record<BubbleTokenName, string>> {
  const rgb = hexToRgb(hex);
  if (!rgb) return {};
  const fill = rgbToHex(rgb);
  const fg = readableOn(rgb);
  return role === "user"
    ? { "--verb-bubble-user": fill, "--verb-bubble-user-fg": fg }
    : { "--verb-bubble-assistant": fill, "--verb-bubble-assistant-fg": fg };
}

/** Whichever of near-black or near-white actually clears contrast on `bg`. */
function readableOn(bg: Rgb): string {
  const DARK = "#1c1917";
  const LIGHT = "#fffdf7";
  return contrastRatio(bg, hexToRgb(DARK)!) >= contrastRatio(bg, hexToRgb(LIGHT)!) ? DARK : LIGHT;
}
